import React from 'react';
import { Shield, Heart, Compass, Award, Users, Stethoscope, Lock, Building2, GraduationCap, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import Button from '../components/Button';
import CTASection from '../components/CTASection';

export default function About({ onOpenConsultation }) {
  const coreValues = [
    {
      title: "Compassion & Human Dignity",
      description: "Every individual deserves to be treated with respect, understanding, and dignity. We provide a supportive environment where patients feel heard, valued, and cared for.",
      icon: Heart,
    },
    {
      title: "Professional & Evidence-Based Care",
      description: "Our psychiatric treatment and mental healthcare approach is guided by professional clinical practices, careful assessment, and treatment plans based on each individual's needs.",
      icon: Stethoscope,
    },
    {
      title: "Privacy, Safety & Comfort",
      description: "We maintain a safe and respectful environment with appropriate medical care, patient privacy, and attention to the comfort and wellbeing of every individual.",
      icon: Lock,
    },
    {
      title: "Family Support & Involvement",
      description: "Families play an important role in recovery. We provide guidance and support to help families understand mental health concerns and become positive partners in the recovery journey.",
      icon: Users,
    },
    {
      title: "Focus on Long-Term Recovery",
      description: "Recovery goes beyond managing symptoms. Our rehabilitation approach helps individuals build confidence, develop life skills, improve daily functioning, and work towards greater independence.",
      icon: Compass,
    },
    {
      title: "Honest & Respectful Communication",
      description: "We believe in clear communication and responsible care. Patients and families are given appropriate information to help them understand treatment, progress, and the recovery process.",
      icon: Award,
    },
  ];

  return (
    <div className="bg-canvas overflow-x-hidden">
      <SEO
        title="About UMED | Institutional Care, Mission & Vision"
        description="Learn about UMED Mental Hospital, Deaddiction and Psychiatric Rehabilitation Centre. Our story, founding vision, multidisciplinary care model, and values."
      />

      {/* Page Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
              <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                ABOUT UMED HEALTHCARE INSTITUTION
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-tight">
              A Safe Space for <span className="text-[#2A9D8F]">Mental Health, Recovery</span> & Renewal.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#D9E8E5] leading-relaxed">
              UMED provides compassionate psychiatric care, deaddiction treatment, psychotherapy, and rehabilitation in a safe and supportive environment—helping individuals and families move towards lasting recovery.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story & Founding Vision */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <SectionHeading
                badge="OUR STORY & ORIGIN"
                title="Bringing Compassionate Mental Healthcare Closer to Those Who Need It"
              />

              <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">
                UMED Mental Hospital, Deaddiction and Psychiatric Rehabilitation Centre was established with a simple purpose—to provide quality mental healthcare in a safe, respectful, and supportive environment.
              </p>

              <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">
                Mental health concerns, psychiatric conditions, and substance dependence can affect individuals, relationships, and everyday life. We believe that timely treatment, professional care, and the right support can make recovery possible.
              </p>

              <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">
                At UMED, psychiatric treatment, counselling, psychotherapy, deaddiction care, and rehabilitation come together under one care approach. Our focus is not only on managing symptoms, but also on helping individuals rebuild confidence, relationships, independence, and a better quality of life.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Button onClick={onOpenConsultation} variant="primary" size="md" className="w-full sm:w-auto">
                  Talk to Our Care Team
                </Button>
                <Button to="/facility" variant="secondary" size="md" className="w-full sm:w-auto">
                  Explore Our Facilities
                </Button>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-3xl overflow-hidden shadow-elevated border border-[#D9E8E5] aspect-[4/3] bg-[#123B5D]">
                <img
                  src="/umed-building.jpg"
                  alt="UMED Hospital & Rehabilitation Centre Building"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Vision, Mission & Care Philosophy */}
      <section className="py-20 lg:py-28 bg-[#F5FAF9] border-y border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            <div className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-[#F5FAF9] text-[#0F766E] flex items-center justify-center mb-6 border border-[#D9E8E5]">
                  <Compass className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-2xl text-[#123B5D] font-normal mb-3">
                  Our Vision
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  To create a trusted centre for mental health and deaddiction care where every individual can access compassionate treatment, rebuild confidence, and move towards a healthier and more independent life.
                </p>
              </div>
            </div>

            <div className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-[#F5FAF9] text-[#0F766E] flex items-center justify-center mb-6 border border-[#D9E8E5]">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-2xl text-[#123B5D] font-normal mb-3">
                  Our Mission
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  To provide safe, evidence-based psychiatric treatment, deaddiction care, counselling, and rehabilitation in a compassionate environment, while supporting individuals and families throughout the recovery journey.
                </p>
              </div>
            </div>

            <div className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-[#F5FAF9] text-[#0F766E] flex items-center justify-center mb-6 border border-[#D9E8E5]">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-2xl text-[#123B5D] font-normal mb-3">
                  Care Philosophy
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  We believe mental healthcare should treat the whole person, not just the condition. Our care focuses on dignity, understanding, personalized treatment, family support, and long-term recovery.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Director & Clinical Leadership Profile */}
      <section className="py-20 lg:py-28 bg-white border-b border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <SectionHeading
            badge="LEADERSHIP & CLINICAL DIRECTION"
            title="Director's Profile"
            subtitle="Guiding UMED with 7+ years of specialized psychiatric care, de-addiction science, and clinical leadership."
            className="mb-14"
          />

          <div className="bg-[#F5FAF9] rounded-3xl border border-[#D9E8E5] shadow-card p-6 sm:p-10 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
              
              {/* Left Column: Photo & Key Roles */}
              <div className="lg:col-span-4 space-y-6">
                <div className="relative rounded-3xl overflow-hidden shadow-elevated border border-[#D9E8E5] bg-[#123B5D] aspect-[4/5] max-w-sm mx-auto lg:max-w-none">
                  <img
                    src="/dr-sanad-pawar.png"
                    alt="Dr. Sanad Pawar - Director UMED"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#123B5D]/90 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-[#0F766E]/90 text-white border border-white/20 backdrop-blur-sm">
                      <Award className="w-3.5 h-3.5 text-[#2A9D8F]" />
                      <span>7 Years Clinical Experience</span>
                    </span>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-sm space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-[#0F766E] flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#123B5D] block">
                        Director & Founder:
                      </span>
                      <ul className="text-xs text-[#64748B] space-y-1 font-medium mt-1">
                        <li>&bull; UMED Rehabilitation Centre, Miraj</li>
                        <li>&bull; UMED Mental Health Clinic & Counselling Centre, Miraj</li>
                      </ul>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-[#D9E8E5] flex items-start gap-3">
                    <Stethoscope className="w-5 h-5 text-[#0F766E] flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#123B5D] block">
                        Consultant Psychiatrist:
                      </span>
                      <p className="text-xs text-[#64748B] font-medium mt-1">
                        Prakash Institute For Medical Sciences, Ishwarpur
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Credentials, Career & Specializations */}
              <div className="lg:col-span-8 space-y-8">
                
                {/* Header Title Block */}
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5FAF9] text-[#0F766E] border border-[#D9E8E5] text-xs font-semibold mb-3">
                    <span>Medical Director & Senior Consultant</span>
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#123B5D] font-normal">
                    Dr. SANAD PAWAR
                  </h2>
                  <p className="text-sm font-bold text-[#0F766E] mt-1 tracking-wide">
                    M.B.B.S, D.P.M, M.I.P.S
                  </p>
                  <p className="text-xs sm:text-sm text-[#64748B] italic mt-1.5 font-medium">
                    (Consultant Psychiatrist, De-addiction Specialist & Sexologist)
                  </p>
                </div>

                {/* Clinical Career & Past Appointments */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-[#0F766E]" />
                    <span>Clinical Career & Past Experience</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#18323A]">
                    <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                      <span className="font-bold text-[#123B5D] block mb-1">Ex. Consultant Psychiatrist:</span>
                      <ul className="space-y-1 text-[#64748B]">
                        <li>&bull; Niramay Rehabilitation Centre, Pune</li>
                        <li>&bull; Unique Foundation & De-addiction Centre, Pune</li>
                        <li>&bull; Manoshkati De-addiction Centre, Pune</li>
                      </ul>
                    </div>

                    <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm space-y-2">
                      <div>
                        <span className="font-bold text-[#123B5D] block">Assistant Psychiatrist:</span>
                        <span className="text-[#64748B]">Poona Hospital & Research Centre, Pune</span>
                      </div>
                      <div className="pt-1.5 border-t border-[#D9E8E5]">
                        <span className="font-bold text-[#123B5D] block">Senior Resident Psychiatrist:</span>
                        <span className="text-[#64748B]">K.E.M Hospital & Research Centre, Pune</span>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                      <span className="font-bold text-[#123B5D] block mb-1">Junior Resident Psychiatrist:</span>
                      <span className="text-[#64748B]">Jagruti Rehabilitation Centre, Pune</span>
                    </div>

                    <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                      <span className="font-bold text-[#123B5D] block mb-1">M.B.B.S Education:</span>
                      <span className="text-[#64748B]">Smt. Kashibai Navale Medical College & General Hospital, Pune</span>
                    </div>
                  </div>
                </div>

                {/* Specialization Areas */}
                <div className="space-y-4 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0F766E]" />
                    <span>Specialization & Clinical Expertise</span>
                  </h4>

                  <div className="space-y-3 text-xs">
                    {/* Category 1: Deaddiction */}
                    <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                      <span className="font-bold text-[#123B5D] block mb-2 text-xs uppercase tracking-wide">
                        ● Deaddiction:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {["Alcohol", "Tobacco", "Cigarette Smoking", "Drugs", "Mobile", "Opium", "Cannabis"].map((item, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-md bg-[#F5FAF9] text-[#0F766E] font-semibold border border-[#D9E8E5]">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Category 2: Mental Disorders */}
                    <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                      <span className="font-bold text-[#123B5D] block mb-2 text-xs uppercase tracking-wide">
                        ● Mental Disorder Treatment:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {["Schizophrenia", "Bipolar Mood Disorder", "Psychosis", "Sleep Disorder", "Anxiety", "Depression", "Stress Management"].map((item, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-md bg-[#F5FAF9] text-[#123B5D] border border-[#D9E8E5] font-semibold">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Category 3: Brain, Intellectual & Sexual Disorders */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                        <span className="font-bold text-[#123B5D] block mb-1 text-xs uppercase tracking-wide">
                          ● Intellectual Disability:
                        </span>
                        <p className="text-[#64748B] leading-relaxed">
                          Treatment & Care for Patients with Mental Retardation / Intellectual Disability.
                        </p>
                      </div>

                      <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                        <span className="font-bold text-[#123B5D] block mb-1 text-xs uppercase tracking-wide">
                          ● Brain Disorders:
                        </span>
                        <p className="text-[#64748B] leading-relaxed">
                          Alzheimer's Disease, Dementia, Post stroke behavioural Problems, Cognitive Impairment.
                        </p>
                      </div>

                      <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-sm">
                        <span className="font-bold text-[#123B5D] block mb-1 text-xs uppercase tracking-wide">
                          ● Sexual Disorders:
                        </span>
                        <p className="text-[#64748B] leading-relaxed">
                          Erectile Dysfunction, Premature Ejaculation, Night Fall, Reduced Libido.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="INSTITUTIONAL PILLARS"
            title="The Values That Shape Our Care"
            align="center"
            className="mb-16"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((val, idx) => {
              const IconComp = val.icon;
              return (
                <div key={idx} className="p-8 rounded-2xl bg-white border border-[#D9E8E5] shadow-card hover:shadow-medium transition-all">
                  <div className="w-10 h-10 rounded-xl bg-[#F5FAF9] text-[#0F766E] flex items-center justify-center mb-5 border border-[#D9E8E5]">
                    <IconComp className="w-5 h-5 text-[#0F766E]" />
                  </div>
                  <h4 className="font-serif text-xl text-[#123B5D] font-normal mb-2">
                    {val.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-[#64748B] leading-relaxed">
                    {val.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>



      {/* CTA */}
      <CTASection onOpenConsultation={onOpenConsultation} />
    </div>
  );
}
